import { Box, Typography, Stack, Alert } from "@mui/material";
import { useState } from "react";
import { Spinner, Button } from "react-bootstrap";

import {
  useAddNoteMutation,
  useUpdateNoteMutation,
} from "api/memberPortal/notes/notes.api";
import { useAppForm } from "components/Forms/AppForm";
import DialogModal from "components/Forms/components/modals/DialogModal";
import { useAuth } from "hooks/useAuth";

import {
  EarNoteFormProps,
  BaseNoteFormValues,
  BaseNoteFormSchema,
} from "./NoteForm.schema";

const EarNoteForm = ({
  show,
  handleClose,
  note,
  earReview,
  replyTo,
}: EarNoteFormProps) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerUpdateNote] = useUpdateNoteMutation();
  const [triggerAddNote] = useAddNoteMutation();

  const earNoteForm = useAppForm({
    defaultValues: {
      title: null,
      note: note?.note,
    } as BaseNoteFormValues,
    validators: {
      onSubmit: BaseNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        if (note?.note_id) {
          await triggerUpdateNote({
            noteId: note.note_id,
            body: {
              ...value,
            },
          });
        } else {
          await triggerAddNote({
            ...value,
            note_type: "ear-review",
            note_type_id: earReview.earid,
            visible_to_smes: false,
            visible_to_labs: false,
            visible_to_validators: false,
            internal_only: false,
            in_response_to: replyTo?.note_id,
          });
        }
        handleClose();
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error adding note. Please try again.");
        setLoading(false);
      }
    },
  });

  return (
    <DialogModal
      title={`+ NOTE for EAR Review ${earReview?.earid}`}
      open={show}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            earNoteForm.handleSubmit();
          }}
        >
          {/* WARN: Custom styling from pre standardization! */}
          {/* TODO: Standardize when you come across this!  */}
          <Typography id="modal-modal-description" variant="body1">
            <Typography variant="body1" fontWeight="bold" className="mb-3">
              Submitter: {currentUser.username}
            </Typography>
            <Stack spacing={3}>
              <h4>
                VID {typeof earReview?.pid === "object" && earReview?.pid?.v_id}
              </h4>
              <earNoteForm.AppField name="note">
                {(field) => <field.HtmlEditor label="Overview" />}
              </earNoteForm.AppField>
            </Stack>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              type={loading ? "button" : "submit"}
              data-cy="submit-note"
            >
              {loading && <Spinner animation="border" size="sm" />}
              {note?.note_id ? "Update" : "Add"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </form>
      </Box>
    </DialogModal>
  );
};

export default EarNoteForm;
